import * as Yup from 'yup';
import { FormattedMessage } from 'react-intl';
import { Formik, useFormikContext } from 'formik';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import {
    PoolBanner,
    PoolButton,
    PoolFormikTextfield,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
} from '@synergy/components-next';
import { Stack } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useState } from 'react';

import { BonusSettlementSupplierDetailFormValues } from '../models/bonus-settlement-supplier-detail-form-values';
import { updateAllPoolBonus } from '../store/bonus-settlements-supplier-detail-actions';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

interface BonusSettlementSupplierDetailChangeAllBonusModalValues {
    comment: string;
}

interface BonusSettlementSupplierDetailChangeAllBonusModalProps {
    open: boolean;
    onClose: () => void;
}

export const BonusSettlementSupplierDetailChangeAllBonusModal = (
    props: BonusSettlementSupplierDetailChangeAllBonusModalProps
) => {
    const { open, onClose } = props;
    const dispatch = useThunkDispatch();
    const { bonusSettlementId, id: supplierBonusSettlementId } = useParams<{ bonusSettlementId: string; id: string }>();
    const [showBanner, setShowBanner] = useState(true);
    const supplierDetailFormikContext = useFormikContext<BonusSettlementSupplierDetailFormValues>();

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setShowBanner(true);
        }, 500);
    };

    const handleSubmit = (values: BonusSettlementSupplierDetailChangeAllBonusModalValues) => {
        if (!supplierBonusSettlementId || !bonusSettlementId) {
            return;
        }
        dispatch(
            updateAllPoolBonus({
                bonusSettlementId: +bonusSettlementId,
                supplierBonusSettlementId: +supplierBonusSettlementId,
                noteToCustomer: values.comment,
            })
        );
        supplierDetailFormikContext.values.customerBonusSettlements.forEach((_customerBonusSettlement, index) => {
            supplierDetailFormikContext.setFieldValue(
                `customerBonusSettlements.${index}.noteToCustomer`,
                values.comment
            );
            supplierDetailFormikContext.setFieldValue(`customerBonusSettlements.${index}.canEditBonus`, true);
        });

        handleClose();
    };

    const initialValues: BonusSettlementSupplierDetailChangeAllBonusModalValues = {
        comment: '',
    };

    const maxLengthError = 'app.error.maxLength';

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={Yup.object().shape({
                comment: Yup.string().required('app.error.required').max(150, maxLengthError),
            })}
        >
            {formikProps => (
                <PaloNextThemeProvider>
                    <PoolModal open={open} onClose={handleClose}>
                        <PoolModalTitle>
                            <FormattedMessage id="app.bonusSettlements.supplierDetail.changeAllBonus" />
                        </PoolModalTitle>
                        <PoolModalContent>
                            <Stack gap={2}>
                                <FormattedMessage id="app.bonusSettlements.supplierDetail.changeAllBonus.contentText" />
                                <PoolFormikTextfield
                                    name="comment"
                                    required={true}
                                    minRows={3}
                                    multiline={true}
                                    label={<FormattedMessage id="app.bonusSettlements.supplierDetail.noteToCustomer" />}
                                    hint={
                                        <FormattedMessage
                                            id="app.bonusSettlements.supplierDetail.noteToCustomer.helperText"
                                            values={{ value: formikProps.values.comment?.length ?? 0 }}
                                        />
                                    }
                                    helperTextValues={{ value: 150 }}
                                />
                                {showBanner && (
                                    <PoolBanner
                                        variant="outlined"
                                        severity="warning"
                                        onClose={() => setShowBanner(false)}
                                    >
                                        <FormattedMessage id="app.bonusSettlements.supplierDetail.changeAllBonus.bannerText" />
                                    </PoolBanner>
                                )}
                            </Stack>
                        </PoolModalContent>
                        <PoolModalActions>
                            <PoolButton variant="outlined" onClick={handleClose}>
                                <FormattedMessage id="app.common.cancel" />
                            </PoolButton>
                            <PoolButton onClick={() => formikProps.handleSubmit()}>
                                <FormattedMessage id="app.common.save" />
                            </PoolButton>
                        </PoolModalActions>
                    </PoolModal>
                </PaloNextThemeProvider>
            )}
        </Formik>
    );
};
